import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";

const STORAGE_KEY = "cecit_cookie_consent_v1";

const DEFAULT_CONSENT = {
  necessary: true,
  analytics: false,
  marketing: false,
  functional: false,
  method: null,
  ts: null,
};

const CookieContext = createContext(null);

/**
 * GDPR cookie consent provider. Persists to localStorage and also POSTs an
 * anonymised record to /api/cookie-consent so the CECIT team keeps an
 * evidence trail. Necessary cookies (session, security, i18n selection)
 * are always granted.
 */
export function CookieConsentProvider({ children }) {
  const [consent, setConsent] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return { ...DEFAULT_CONSENT, ...JSON.parse(raw) };
    } catch (err) {
      // Corrupt JSON in localStorage — fall back to defaults but leave a trace
      // for anyone digging into a user report.
      console.warn("cookie-consent: could not parse stored consent", err);
    }
    return DEFAULT_CONSENT;
  });
  const [bannerOpen, setBannerOpen] = useState(() => {
    try {
      return !localStorage.getItem(STORAGE_KEY);
    } catch (err) {
      console.warn("cookie-consent: localStorage unavailable", err);
      return true;
    }
  });

  const persist = useCallback((next) => {
    const withTs = { ...next, ts: new Date().toISOString() };
    setConsent(withTs);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(withTs));
    } catch (err) {
      // Quota exceeded or private-mode restrictions — non-fatal.
      console.warn("cookie-consent: could not persist consent", err);
    }
    // Fire-and-forget backend record — never block UI
    api.post("/cookie-consent", {
      necessary: true,
      analytics: !!withTs.analytics,
      marketing: !!withTs.marketing,
      functional: !!withTs.functional,
      method: withTs.method || "customize",
    }).catch((err) => console.warn("cookie-consent: backend record failed", err?.message || err));
    setBannerOpen(false);
  }, []);

  const acceptAll = useCallback(() => {
    persist({ necessary: true, analytics: true, marketing: true, functional: true, method: "accept_all" });
  }, [persist]);

  const rejectAll = useCallback(() => {
    persist({ necessary: true, analytics: false, marketing: false, functional: false, method: "reject_non_essential" });
  }, [persist]);

  const savePrefs = useCallback((partial) => {
    persist({
      necessary: true,
      analytics: !!partial.analytics,
      marketing: !!partial.marketing,
      functional: !!partial.functional,
      method: "customize",
    });
  }, [persist]);

  const openPreferences = useCallback(() => setBannerOpen(true), []);

  const value = useMemo(
    () => ({ consent, bannerOpen, acceptAll, rejectAll, savePrefs, openPreferences }),
    [consent, bannerOpen, acceptAll, rejectAll, savePrefs, openPreferences]
  );

  return <CookieContext.Provider value={value}>{children}</CookieContext.Provider>;
}

export function useCookieConsent() {
  const ctx = useContext(CookieContext);
  if (!ctx) throw new Error("useCookieConsent must be used inside CookieConsentProvider");
  return ctx;
}

/**
 * GA4Loader — only injects the gtag script AFTER the user has granted the
 * analytics category. Reads the Measurement ID from /api/settings (public).
 */
export function GA4Loader() {
  const { consent } = useCookieConsent();
  const [measurementId, setMeasurementId] = useState("");

  useEffect(() => {
    api.get("/settings").then((r) => setMeasurementId(r.data?.ga4_measurement_id || "")).catch(() => {});
  }, []);

  useEffect(() => {
    if (!consent.analytics || !measurementId) return;
    if (document.querySelector(`script[data-ga4="${measurementId}"]`)) return;
    const s1 = document.createElement("script");
    s1.async = true;
    s1.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    s1.setAttribute("data-ga4", measurementId);
    document.head.appendChild(s1);
    const s2 = document.createElement("script");
    s2.setAttribute("data-ga4-init", measurementId);
    s2.text = `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', '${measurementId}', { anonymize_ip: true });`;
    document.head.appendChild(s2);
  }, [consent.analytics, measurementId]);

  return null;
}
